import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  ArrowRight,
  CalendarDays,
  Mail,
  MapPin,
  Phone,
  ShoppingBag,
  Sparkles,
  TicketPercent,
  Wallet,
} from "lucide-react";
import { http } from "../services/http";

type ProfileApi = {
  user: {
    userId: number;
    username: string;
    email: string;
    phone: string | null;
    pointAccumulated: number | null;
    pointUsed: number | null;
    createdAt: string;
  };
  pointValue: number;
  availablePoints: number;
  pointToMoneyRate: number;
  vouchers: any[];
  printedVouchers: any[];
};

type OrderApi = {
  orderId: number;
  userId: number;
  orderDate: string;
  totalPrice: number;
  subtotal: number;
  statusId: number;
  voucherCode: string | null;
  discountAmount: number;
  shippingFee: number;
  loyaltyPointDiscount: number;
  receiverName: string;
  phone: string;
  email: string;
  address: string;
  paymentMethod: string;
  paymentStatus: string;
  shippingStatus: string;
  orderDetails: Array<{
    productName: string;
    imageUrl: string;
    quantity: number;
    price: number;
    size: string;
    color: string;
    subtotal: number;
  }>;
};

const RAW_API_URL = process.env.REACT_APP_API_URL || "";
const API_URL = RAW_API_URL.replace(/\/+$/, "");

function cn(...x: Array<string | false | undefined | null>) {
  return x.filter(Boolean).join(" ");
}

function resolveImg(url?: string | null) {
  if (!url) return "/images/placeholder.jpg";
  if (/^https?:\/\//i.test(url)) return url;
  if (url.startsWith("/")) return API_URL ? `${API_URL}${url}` : url;
  return API_URL ? `${API_URL}/${url}` : url;
}

function formatVND(n: number, locale: string) {
  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency: "VND",
    maximumFractionDigits: 0,
  }).format(n ?? 0);
}

function initials(s: string) {
  const t = s.trim();
  if (!t) return "OD";
  const parts = t.split(/\s+/);
  const a = parts[0]?.[0] || "";
  const b = parts[parts.length - 1]?.[0] || "";
  return (a + b).toUpperCase();
}

function Card({
  children,
  className = "",
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <section
      className={cn(
        "rounded-[30px] border border-brand-brown/10 bg-white/72 backdrop-blur-xl",
        "shadow-[0_24px_80px_rgba(43,31,26,0.06)]",
        className,
      )}
    >
      {children}
    </section>
  );
}

function StatBox(props: {
  label: string;
  value: React.ReactNode;
  icon?: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        "rounded-[22px] border border-brand-brown/10 bg-white/82 p-4",
        props.className,
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-[10px] font-medium uppercase tracking-[0.16em] text-brand-brown/42">
            {props.label}
          </p>
          <div className="mt-2 text-sm font-medium text-brand-brown">
            {props.value}
          </div>
        </div>

        {props.icon && (
          <div className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-brand-brown/10 bg-brand-ivory/75 text-brand-brown/62">
            {props.icon}
          </div>
        )}
      </div>
    </div>
  );
}

function LoadingCard() {
  return (
    <div className="overflow-hidden rounded-[28px] border border-brand-brown/10 bg-white/72 p-5 shadow-[0_20px_60px_rgba(43,31,26,0.05)]">
      <div className="animate-pulse space-y-4">
        <div className="h-4 w-28 rounded bg-brand-brown/10" />
        <div className="h-8 w-2/3 rounded bg-brand-brown/10" />
        <div className="h-20 rounded-[20px] bg-brand-brown/8" />
      </div>
    </div>
  );
}

export default function ProfilePage() {
  const { i18n } = useTranslation();
  const isVN = i18n.language?.startsWith("vi");
  const locale = isVN ? "vi-VN" : "en-US";

  const text = {
    kicker: "OWL DREAM / ACCOUNT",
    title: isVN ? "Tài khoản của tôi" : "My account",
    subtitle: isVN
      ? "Quản lý thông tin cá nhân, điểm và đơn hàng trong một không gian gọn, sáng và dễ nhìn hơn."
      : "Manage profile, points, and orders in a cleaner, brighter account space.",
    edit: isVN ? "Cập nhật thông tin" : "Edit profile",
    shop: isVN ? "Tiếp tục mua sắm" : "Continue shopping",
    address: isVN ? "Địa chỉ" : "Address",
    profile: isVN ? "Hồ sơ" : "Profile",
    phone: isVN ? "Số điện thoại" : "Phone",
    email: isVN ? "Email" : "Email",
    availablePoints: isVN ? "Điểm khả dụng" : "Available points",
    currentValue: isVN ? "Giá trị hiện tại" : "Current value",
    conversion: isVN ? "Tỷ lệ quy đổi" : "Point conversion",
    history: isVN ? "Tích luỹ / đã dùng" : "Accumulated / used",
    vouchers: isVN ? "Voucher" : "Vouchers",
    memberSince: isVN ? "Tham gia từ" : "Member since",
    orders: isVN ? "Đơn hàng của tôi" : "My orders",
    ordersDesc: isVN
      ? "Xem trạng thái, thanh toán và sản phẩm trong từng đơn hàng."
      : "Review status, payment, and products in each order.",
    orderCount: (n: number) => (isVN ? `${n} đơn` : `${n} orders`),
    total: isVN ? "Tổng" : "Total",
    ship: isVN ? "Ship" : "Shipping",
    details: isVN ? "Xem chi tiết" : "View details",
    noOrders: isVN ? "Bạn chưa có đơn hàng nào" : "You have no orders yet",
    noOrdersDesc: isVN
      ? "Khi có đơn đầu tiên, chúng sẽ xuất hiện ở đây."
      : "Your first orders will appear here.",
    loading: isVN
      ? "Đang tải thông tin tài khoản..."
      : "Loading account information...",
    error: isVN
      ? "Không thể tải thông tin tài khoản"
      : "Unable to load account information",
    noData: isVN ? "Không có dữ liệu" : "No data available",
    pointsShort: isVN ? "điểm" : "pts",
    memberFallback: isVN ? "Thành viên Owl Dream" : "Owl Dream member",
    statusPending: isVN ? "Chờ xác nhận" : "Pending",
    statusConfirmed: isVN ? "Đã xác nhận" : "Confirmed",
    statusFallback: (id: number) =>
      isVN ? `Trạng thái #${id}` : `Status #${id}`,
    size: isVN ? "Size" : "Size",
  };

  const [profile, setProfile] = useState<ProfileApi | null>(null);
  const [orders, setOrders] = useState<OrderApi[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let mounted = true;

    Promise.all([http.get("/api/Users/profile"), http.get("/api/Order/user-orders")])
      .then(([p, o]) => {
        if (!mounted) return;
        setProfile(p.data);
        setOrders(Array.isArray(o.data) ? o.data : []);
      })
      .catch(() => {
        if (!mounted) return;
        setError(text.error);
      })
      .finally(() => mounted && setLoading(false));

    return () => {
      mounted = false;
    };
  }, [text.error]);

  const user = profile?.user;

  const displayName = useMemo(
    () => user?.username || text.memberFallback,
    [user?.username, text.memberFallback],
  );

  const memberSince = useMemo(() => {
    if (!user?.createdAt) return "—";
    try {
      return new Date(user.createdAt).toLocaleDateString(locale, {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch {
      return "—";
    }
  }, [user?.createdAt, locale]);

  const statusLabel = (o: OrderApi) => {
    if (o.statusId === 11) return text.statusPending;
    if (o.statusId === 10) return text.statusConfirmed;
    if (o.paymentStatus) return o.paymentStatus;
    if (o.shippingStatus) return o.shippingStatus;
    return text.statusFallback(o.statusId);
  };

  const statusBadgeCls = (o: OrderApi) => {
    if (o.statusId === 11) return "bg-amber-50 text-amber-700 ring-amber-200";
    if (o.statusId === 10) return "bg-emerald-50 text-emerald-700 ring-emerald-200";
    return "bg-neutral-50 text-neutral-600 ring-neutral-200";
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#f7f0e5] text-brand-brown">
        <div className="mx-auto max-w-7xl px-4 pb-16 pt-28 sm:px-6 lg:pt-32">
          <div className="mb-8">
            <div className="h-4 w-40 animate-pulse rounded bg-brand-brown/10" />
            <div className="mt-4 h-10 w-72 animate-pulse rounded bg-brand-brown/10" />
          </div>

          <div className="grid gap-6 xl:grid-cols-[0.94fr_1.06fr]">
            <LoadingCard />
            <LoadingCard />
          </div>
        </div>
      </div>
    );
  }

  if (error || !profile || !user) {
    return (
      <div className="min-h-screen bg-[#f7f0e5] text-brand-brown">
        <div className="mx-auto max-w-4xl px-4 pb-16 pt-28 text-center sm:px-6 lg:pt-32">
          <div className="rounded-[28px] border border-red-500/20 bg-red-500/5 p-8 text-sm text-red-600">
            {error || text.noData}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f7f0e5] text-brand-brown">
      <div className="pointer-events-none fixed inset-0 -z-10 overflow-hidden">
        <div className="absolute inset-0 bg-[#f7f0e5]" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(205,170,111,0.08),transparent_22%),radial-gradient(circle_at_bottom_right,rgba(43,31,26,0.05),transparent_28%)]" />
        <div className="absolute left-[-8%] top-[6%] h-[280px] w-[280px] rounded-full bg-brand-gold/8 blur-3xl" />
        <div className="absolute bottom-[-10%] right-[-8%] h-[240px] w-[240px] rounded-full bg-brand-brown/7 blur-3xl" />
      </div>

      {/* HERO */}
      <section className="border-b border-brand-brown/10 pt-24 sm:pt-28 lg:pt-32">
        <div className="mx-auto max-w-7xl px-4 pb-8 sm:px-6 sm:pb-10">
          <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
            <div className="max-w-3xl">
              <div className="inline-flex items-center gap-2 rounded-full border border-brand-brown/10 bg-white/72 px-4 py-2 text-[10px] font-medium uppercase tracking-[0.18em] text-brand-brown/62 shadow-[0_12px_30px_rgba(43,31,26,0.04)]">
                <Sparkles className="h-3.5 w-3.5 text-brand-gold" />
                {text.kicker}
              </div>

              <h1 className="mt-5 text-[34px] font-medium leading-[0.96] tracking-[0.01em] text-brand-brown sm:text-[48px] lg:text-[60px]">
                {text.title}
              </h1>

              <p className="mt-4 max-w-2xl text-sm leading-7 text-brand-brown/58 sm:text-[15px] sm:leading-8">
                {text.subtitle}
              </p>
            </div>

            <div className="flex flex-wrap gap-3">
              <Link
                to="/profile/edit"
                className="inline-flex h-11 items-center justify-center rounded-full border border-brand-brown/12 bg-white/80 px-5 text-[10px] font-medium uppercase tracking-[0.14em] text-brand-brown transition hover:border-brand-brown hover:bg-brand-brown hover:text-brand-ivory"
              >
                {text.edit}
              </Link>

              <Link
                to="/shop"
                className="inline-flex h-11 items-center justify-center rounded-full bg-brand-brown px-5 text-[10px] font-medium uppercase tracking-[0.14em] text-brand-ivory transition hover:opacity-90"
              >
                {text.shop}
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CONTENT */}
      <section className="mx-auto max-w-7xl px-4 py-8 sm:px-6 sm:py-10">
        <div className="grid gap-6 xl:grid-cols-[0.94fr_1.06fr]">
          {/* PROFILE */}
          <Card className="p-6 sm:p-7">
            <div className="flex items-center gap-4">
              <div className="flex h-16 w-16 items-center justify-center rounded-[22px] bg-brand-brown text-lg font-semibold text-brand-ivory shadow-[0_18px_36px_rgba(43,31,26,0.18)]">
                {initials(displayName)}
              </div>

              <div className="min-w-0">
                <p className="text-[10px] font-medium uppercase tracking-[0.18em] text-brand-brown/42">
                  {text.profile}
                </p>
                <p className="mt-2 truncate text-[24px] font-medium leading-none text-brand-brown">
                  {displayName}
                </p>
                <p className="mt-2 truncate text-sm text-brand-brown/58">
                  {user.email}
                </p>
              </div>
            </div>

            <div className="mt-6 grid gap-3 sm:grid-cols-2">
              <StatBox
                label={text.phone}
                value={user.phone || "—"}
                icon={<Phone className="h-4 w-4" />}
              />

              <StatBox
                label={text.email}
                value={user.email}
                icon={<Mail className="h-4 w-4" />}
              />

              <StatBox
                label={text.availablePoints}
                value={
                  <span>
                    {profile.availablePoints.toLocaleString("vi-VN")} {text.pointsShort}
                  </span>
                }
                icon={<Wallet className="h-4 w-4" />}
              />

              <StatBox
                label={text.currentValue}
                value={formatVND(profile.pointValue, locale)}
                icon={<Sparkles className="h-4 w-4" />}
              />

              <StatBox
                label={text.conversion}
                value={
                  <span>
                    1 {text.pointsShort} = {formatVND(profile.pointToMoneyRate, locale)}
                  </span>
                }
                icon={<Wallet className="h-4 w-4" />}
                className="sm:col-span-2"
              />

              <StatBox
                label={text.history}
                value={
                  <span>
                    {(user.pointAccumulated ?? 0).toLocaleString("vi-VN")} /{" "}
                    {(user.pointUsed ?? 0).toLocaleString("vi-VN")}
                  </span>
                }
                icon={<Sparkles className="h-4 w-4" />}
              />

              <StatBox
                label={text.vouchers}
                value={
                  <span>
                    {(profile.vouchers?.length ?? 0) +
                      (profile.printedVouchers?.length ?? 0)}
                  </span>
                }
                icon={<TicketPercent className="h-4 w-4" />}
              />

              <StatBox
                label={text.memberSince}
                value={memberSince}
                icon={<CalendarDays className="h-4 w-4" />}
                className="sm:col-span-2"
              />
            </div>

            <div className="mt-6 grid gap-3 sm:grid-cols-2">
              <Link
                to="/profile/edit"
                className="inline-flex h-11 items-center justify-center rounded-full border border-brand-brown/12 bg-white/80 px-4 text-[10px] font-medium uppercase tracking-[0.14em] text-brand-brown transition hover:border-brand-brown hover:bg-brand-brown hover:text-brand-ivory"
              >
                {text.edit}
              </Link>

              <Link
                to="/address"
                className="inline-flex h-11 items-center justify-center rounded-full border border-brand-brown/12 bg-white/80 px-4 text-[10px] font-medium uppercase tracking-[0.14em] text-brand-brown transition hover:border-brand-brown hover:bg-brand-brown hover:text-brand-ivory"
              >
                <MapPin className="mr-2 h-4 w-4" />
                {text.address}
              </Link>
            </div>
          </Card>

          {/* ORDERS */}
          <Card className="p-6 sm:p-7">
            <div className="flex items-center justify-between gap-4">
              <div>
                <p className="text-[10px] font-medium uppercase tracking-[0.18em] text-brand-brown/42">
                  {text.orders}
                </p>
                <h2 className="mt-2 text-[24px] font-medium text-brand-brown">
                  {text.orders}
                </h2>
                <p className="mt-2 text-sm leading-7 text-brand-brown/58">
                  {text.ordersDesc}
                </p>
              </div>

              <div className="inline-flex h-11 items-center rounded-full border border-brand-brown/10 bg-white/82 px-4 text-[10px] font-medium uppercase tracking-[0.14em] text-brand-brown/62">
                {text.orderCount(orders.length)}
              </div>
            </div>

            {orders.length === 0 ? (
              <div className="mt-6 rounded-[26px] border border-dashed border-brand-brown/15 bg-white/70 p-10 text-center">
                <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-brand-brown/6 text-brand-brown">
                  <ShoppingBag className="h-5 w-5" />
                </div>
                <p className="mt-5 text-base font-medium text-brand-brown">
                  {text.noOrders}
                </p>
                <p className="mt-2 text-sm text-brand-brown/58">
                  {text.noOrdersDesc}
                </p>
              </div>
            ) : (
              <div className="mt-6 space-y-3">
                {orders.map((o) => {
                  const first = o.orderDetails?.[0];

                  return (
                    <Link
                      key={o.orderId}
                      to={`/orders/${o.orderId}`}
                      className="group block rounded-[26px] border border-brand-brown/10 bg-white/82 p-5 transition hover:-translate-y-0.5 hover:border-brand-brown/20 hover:shadow-[0_22px_60px_rgba(43,31,26,0.08)]"
                    >
                      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                        <div className="flex min-w-0 items-center gap-4">
                          {first?.imageUrl ? (
                            <img
                              src={resolveImg(first.imageUrl)}
                              alt={first.productName}
                              className="h-16 w-16 rounded-[18px] object-cover ring-1 ring-brand-brown/10"
                            />
                          ) : (
                            <div className="h-16 w-16 rounded-[18px] bg-brand-brown/5" />
                          )}

                          <div className="min-w-0">
                            <div className="flex flex-wrap items-center gap-3">
                              <p className="truncate text-base font-semibold text-brand-brown">
                                #{o.orderId}
                              </p>

                              <span
                                className={cn(
                                  "inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium ring-1 ring-inset",
                                  statusBadgeCls(o),
                                )}
                              >
                                {statusLabel(o)}
                              </span>
                            </div>

                            <p className="mt-1 truncate text-sm text-brand-brown/58">
                              {new Date(o.orderDate).toLocaleDateString(locale)}
                              <span className="mx-2 text-brand-brown/22">•</span>
                              {o.paymentMethod?.toUpperCase()} — {o.paymentStatus}
                            </p>

                            {first && (
                              <p className="mt-1 truncate text-sm text-brand-brown/72">
                                {first.productName} • {first.color} • {text.size}{" "}
                                {first.size} • x{first.quantity}
                              </p>
                            )}
                          </div>
                        </div>

                        <div className="flex items-end justify-between gap-6 sm:items-center sm:justify-end">
                          <div className="text-right">
                            <p className="text-sm text-brand-brown/48">{text.total}</p>
                            <p className="text-lg font-semibold text-brand-brown">
                              {formatVND(o.totalPrice, locale)}
                            </p>
                            {(o.shippingFee ?? 0) > 0 && (
                              <p className="text-xs text-brand-brown/45">
                                {text.ship}: {formatVND(o.shippingFee, locale)}
                              </p>
                            )}
                          </div>

                          <span className="inline-flex items-center gap-2 text-sm font-medium text-brand-brown transition group-hover:translate-x-0.5">
                            {text.details}
                            <ArrowRight className="h-4 w-4" />
                          </span>
                        </div>
                      </div>
                    </Link>
                  );
                })}
              </div>
            )}
          </Card>
        </div>
      </section>
    </div>
  );
}